/**
 * Orchestrates memory-poisoning defense on `before_tool_call`:
 *   - memory read  → sync pointer, refresh disk if drifted, score-gate
 *   - memory write → classify + scan + commit via `guardMemoryWrite`
 *   - anything else → returns null, host runs its own audit path
 *
 * Owns the pointer store, file logger, and boot probe internally.
 */
import { promises as fs } from "node:fs";
import path from "node:path";

import { classifyMemoryRead, type ClassifyMemoryReadOptions } from "./read-classifier.js";
import { guardMemoryWrite, type GuardLogger, type GuardMemoryWriteResult } from "./guard.js";
import { PointerStore, defaultPointerPath } from "./pointer-store.js";
import { syncLocalMemory, MemoryIntegrityError } from "./sync.js";
import type { AgentAuth } from "../types.js";
import type { AgentMemoryEntry } from "./chain.js";
import { createFileLogger, defaultPluginLogPath, type WrappedLogger } from "./file-logger.js";

/** Decision the manager returns to the plugin's `before_tool_call` handler. */
export interface HookDecision {
  allow?: boolean;
  block?: boolean;
  blockReason?: string;
  reason?: string;
}

export interface MemoryGuardManagerOptions {
  auth: AgentAuth;
  /** Host workspace root (used to derive default file paths). */
  workspaceDir: string;
  /** Absolute MEMORY.md path. Default `<workspaceDir>/MEMORY.md`. */
  memoryFilePath?: string;
  /** Persistent pointer file. Default `<workspaceDir>/.atbash/memory-pointer.json`. */
  pointerFilePath?: string;
  /** File-backed log. Default `<workspaceDir>/.atbash/plugin.log`. */
  logFilePath?: string;
  /** Optional host-native logger — every event mirrors here as well. */
  hostLogger?: { info: (...args: unknown[]) => void; warn: (...args: unknown[]) => void };
  /** TTL for the local-sync pointer between chain checks. Default 30_000 ms. */
  ttlMs?: number;
  /** Block memory reads when the chain-active version's score is below this. Default 1 (never blocks). */
  rollbackMinScore?: number;
  /** True → return `{block:true}` on defense triggers. False → log and return `null` (audit-only). Default true. */
  enforce?: boolean;
  /** Host-specific tuning of what counts as a memory read. */
  memoryReadClassifier?: ClassifyMemoryReadOptions;
  /** Passed through to `guardMemoryWrite`. Host memory-write tool names override. */
  memoryWriteToolNames?: ReadonlyArray<string>;
  /** Passed through to `guardMemoryWrite` — extra memory path substrings. */
  memoryPathPatterns?: ReadonlyArray<string>;
  /** Passed through to `guardMemoryWrite` — judge endpoint override (defaults to userConfig / DEFAULT_ENDPOINT). */
  judgeEndpoint?: string;
  /** Passed through to `guardMemoryWrite` — self-hosted judge verify pubkey. */
  judgeVerifyPubKey?: string;
  /** Passed through — org name for chain resolution. */
  orgName?: string;
  /** Debug probe in `guardMemoryWrite`. */
  debug?: boolean;
}

const DEFAULT_SYNC_TTL_MS = 30_000;

/**
 * Wire-once, dispatch-many factory. Plugins call once at register(),
 * then feed every `before_tool_call` through `handleBeforeToolCall`.
 */
export class MemoryGuardManager {
  private readonly pointerStore: PointerStore;
  private readonly logger: WrappedLogger;
  private readonly memoryFilePath: string;
  private readonly ttlMs: number;
  private readonly rollbackMinScore: number;
  private readonly enforce: boolean;
  private readonly agentPubkeyHex: string;

  constructor(private readonly opts: MemoryGuardManagerOptions) {
    const workspaceDir = opts.workspaceDir;
    this.memoryFilePath = opts.memoryFilePath ?? path.join(workspaceDir, "MEMORY.md");
    this.pointerStore = new PointerStore(opts.pointerFilePath ?? defaultPointerPath(workspaceDir));
    this.logger = createFileLogger(
      opts.logFilePath ?? defaultPluginLogPath(workspaceDir),
      opts.hostLogger,
    );
    this.ttlMs = opts.ttlMs ?? DEFAULT_SYNC_TTL_MS;
    this.rollbackMinScore = opts.rollbackMinScore ?? 1;
    this.enforce = opts.enforce !== false;
    this.agentPubkeyHex = opts.auth.pubkey;

    this.logger.info(
      `[atbash] guard manager ready — agent=${this.agentPubkeyHex.slice(0, 16)}… org=${opts.orgName ?? "(none)"} memoryFilePath=${this.memoryFilePath} ttl=${this.ttlMs}ms minScore=${this.rollbackMinScore}`,
    );
  }

  /**
   * One-shot chain probe at plugin registration. Refreshes MEMORY.md
   * from chain when drifted and score passes threshold. Fire-and-forget
   * — errors are logged, never thrown.
   */
  async runBootProbe(): Promise<void> {
    try {
      const seed = { activeId: null, checkedAt: 0 };
      const result = await syncLocalMemory(this.opts.auth, seed, { ttlMs: 0, force: true });

      if (!result.drifted && result.pointer.activeId == null) {
        this.logger.info(
          `[atbash] no active memory on chain for agent=${this.agentPubkeyHex.slice(0, 16)}… org=${this.opts.orgName ?? "(none)"} — either the agent isn't registered on this chain or hasn't written any memory. Sync will remain a no-op until a write lands.`,
        );
      } else if (result.drifted && result.current) {
        if (result.current.score < this.rollbackMinScore) {
          this.logger.warn(
            `[atbash] boot sync REFUSED refresh — id=${result.current.id} score=${result.current.score} below threshold ${this.rollbackMinScore}. Leaving MEMORY.md and pointer untouched; next memory read will be blocked.`,
          );
          return;
        }
        this.logger.info(
          `[atbash] boot sync: refreshing local memory — id=${result.current.id} score=${result.current.score}`,
        );
        try {
          await this.writeMemoryAtomic(result.current.content);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          this.logger.warn("[atbash] boot sync write failed (serving whatever's on disk)", { error: msg });
        }
      }
      await this.pointerStore.set(this.agentPubkeyHex, result.pointer);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.logger.warn("[atbash] boot memory sync failed — check chain endpoint / orgName", { error: msg });
    }
  }

  /**
   * Returns a `HookDecision` when the event is a memory read or write
   * (host returns it verbatim to its runtime). Returns `null` when the
   * event isn't memory-related — host falls through to its own audit.
   */
  async handleBeforeToolCall(event: unknown, ctx: unknown): Promise<HookDecision | null> {
    if (classifyMemoryRead(event, ctx, this.opts.memoryReadClassifier)) {
      this.logger.info("[atbash] memory read intercepted — running sync check");
      // Memory reads are content-reads, not agent actions. On the happy path
      // (no drift, no rollback block) the sync handler returns null — promote
      // it to `{ allow: true }` so the host's runtime doesn't re-audit the
      // read via its general judge endpoint.
      const readDecision = await this.handleMemoryRead();
      return readDecision ?? { allow: true };
    }

    const guardLogger: GuardLogger = {
      info: (msg, meta) =>
        this.logger.info(msg, meta && typeof meta === "object" ? (meta as Record<string, unknown>) : undefined),
      warn: (msg, meta) =>
        this.logger.warn(msg, meta && typeof meta === "object" ? (meta as Record<string, unknown>) : undefined),
    };
    const guard = await guardMemoryWrite({
      event,
      ctx,
      auth: this.opts.auth,
      endpoint: this.opts.judgeEndpoint,
      verifyPubKey: this.opts.judgeVerifyPubKey,
      orgName: this.opts.orgName,
      patterns: this.opts.memoryPathPatterns,
      toolNames: this.opts.memoryWriteToolNames,
      enforce: this.enforce,
      debug: this.opts.debug,
      logger: guardLogger,
    });
    return this.mapGuardResult(guard);
  }

  private mapGuardResult(guard: GuardMemoryWriteResult): HookDecision | null {
    if (!guard.handled) return null;
    const d = guard.decision!;
    const sr = guard.scanResult;
    const verdict = sr?.verdict ?? "?";
    const score = sr?.score ?? "?";
    if (d.block) {
      this.logger.warn(
        `[atbash] guardMemoryWrite BLOCKED — verdict=${verdict} score=${score} reason=${(d.reason ?? "").slice(0, 200)}`,
      );
      return {
        block: true,
        blockReason: d.reason ?? "",
        allow: false,
        reason: d.reason,
      };
    }
    this.logger.info(
      `[atbash] guardMemoryWrite ALLOWED — verdict=${verdict} score=${score} committed=${guard.committed === true}`,
    );
    return { allow: true };
  }

  private async handleMemoryRead(): Promise<HookDecision | null> {
    const pointer = await this.pointerStore.get(this.agentPubkeyHex);
    let result;
    try {
      result = await syncLocalMemory(this.opts.auth, pointer, { ttlMs: this.ttlMs });
    } catch (err) {
      if (err instanceof MemoryIntegrityError) {
        const reason = `Memory integrity check failed on id ${err.id} — read blocked.`;
        this.logger.warn("[atbash] MEMORY INTEGRITY FAILURE", { id: err.id, error: err.message });
        if (!this.enforce) return null;
        return { block: true, blockReason: reason, allow: false, reason };
      }
      const msg = err instanceof Error ? err.message : String(err);
      this.logger.warn("[atbash] memory sync failed (serving local copy)", { error: msg });
      return null;
    }

    if (result.drifted) {
      const fresh = result.current as AgentMemoryEntry | null;
      if (fresh) {
        // Score gate BEFORE disk write — poisoned content never touches disk.
        if (fresh.score < this.rollbackMinScore) {
          const reason = `Rolled-back memory version #${fresh.id} scored ${fresh.score} (below threshold ${this.rollbackMinScore}) — read blocked.`;
          this.logger.warn("[atbash] blocking read on low-score rollback", { id: fresh.id, score: fresh.score });
          if (!this.enforce) return null;
          return { block: true, blockReason: reason, allow: false, reason };
        }
        this.logger.info("[atbash] memory drift detected — refreshing local file", {
          id: fresh.id,
          score: fresh.score,
        });
        try {
          await this.writeMemoryAtomic(fresh.content);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          this.logger.warn("[atbash] failed to write refreshed memory (serving old)", { error: msg });
        }
      } else {
        this.logger.info("[atbash] active memory removed on chain", { pubkey: this.agentPubkeyHex });
      }
    }

    await this.pointerStore.set(this.agentPubkeyHex, result.pointer);
    return null;
  }

  private async writeMemoryAtomic(content: string): Promise<void> {
    await fs.mkdir(path.dirname(this.memoryFilePath), { recursive: true });
    const tmp = `${this.memoryFilePath}.${process.pid}.tmp`;
    await fs.writeFile(tmp, content, "utf8");
    await fs.rename(tmp, this.memoryFilePath);
  }
}

export function createMemoryGuardManager(opts: MemoryGuardManagerOptions): MemoryGuardManager {
  return new MemoryGuardManager(opts);
}
